import React from 'react';
import { Link } from 'react-router-dom';

type ButtonVariant =
  | 'secondary'
  | 'solid'
  | 'outline'
  | 'ghost'
  | 'icon'
  | 'featureFlag';

type ButtonProps = {
  variant?: ButtonVariant;
  children: React.ReactNode;
  onClick?: () => void;
  href?: string;
  icon?: string;
  iconPosition?: 'left' | 'right';
  className?: string;
  style?: React.CSSProperties;
  textClassName?: string;
  iconClassName?: string;
  disabled?: boolean;
  ariaLabel?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>; // inherit native button props

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'solid',
      children,
      onClick,
      href,
      icon,
      iconPosition = 'left',
      className = '',
      style,
      textClassName = '',
      iconClassName = '',
      disabled = false,
      ariaLabel,
      ...rest
    },
    ref
  ) => {
    const baseStyle =
      'inline-flex items-center justify-center font-medium text-sm transition ease-in-out duration-150';

    let variantStyle = '';
    switch (variant) {
      case 'secondary':
        variantStyle =
          'bg-white text-[#DA291C] hover:bg-red-100 border border-[#DA291C] w-[126px] h-[48px]';
        break;
      case 'solid':
        variantStyle = 'bg-[#DA291C] text-white hover:bg-[#b32218]';
        break;
      case 'outline':
        variantStyle =
          'text-[#DA291C] border border-[#DA291C] hover:bg-[#DA291C] hover:text-white';
        break;
      case 'ghost':
        variantStyle = 'text-[#DA291C] hover:bg-[#fce7e7]';
        break;
      case 'icon':
        variantStyle = 'text-[#DA291C] hover:bg-[#fce7e7] p-2';
        break;
      case 'featureFlag':
        variantStyle = 'text-[#9E9E9E]';
        break;
      default:
        variantStyle = 'bg-[#DA291C] text-white hover:bg-[#b32218]';
    }

    const combinedClassName = `${baseStyle} ${variantStyle} ${className} ${
      disabled ? 'opacity-50 cursor-not-allowed' : ''
    }`;

    const content = (
      <span className={`flex items-center ${textClassName}`}>
        {icon && iconPosition === 'left' && (
          <img
            src={icon}
            alt="icon"
            className={`mr-2 w-4 h-4 ${iconClassName}`}
          />
        )}
        {children}
        {icon && iconPosition === 'right' && (
          <img
            src={icon}
            alt="icon"
            className={`ml-2 w-4 h-4 ${iconClassName}`}
          />
        )}
      </span>
    );

    if (href) {
      return (
        <Link
          to={href}
          className={combinedClassName}
          style={style}
          aria-label={ariaLabel}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        ref={ref}
        className={combinedClassName}
        onClick={onClick}
        style={style}
        disabled={disabled}
        aria-label={ariaLabel}
        {...rest}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = 'Button'; // ✅ good practice when using forwardRef
export default Button;
